import React from "react";

import { getEnergyClassItem } from "../functions/AssetOverview";
import { useGetEnergyEfficiencyClassesTimelineData } from "../functions/Modernization";
import { isNumeric } from "../utils/dataManipulation";
import { calculateLeft } from "../utils/position";
import { cn } from "../utils/styling";
import { EnergyEfficiencyClass } from "./EnergyEfficiencyClass";

export type DataForChartItem = { primaryModernized?: null | number; year?: number };

export type EnergyClassConfItem = { bgColor: string; max: number; rating: string; textColor: string };

export type YearlyConsumption = { consumption: number; year: number };

type EnergyRun = { classItem: ReturnType<typeof getEnergyClassItem>; endYear: number; startYear: number };

export function dataForChartToYearlyConsumption(
  dataForChart: Array<{ primaryModernized?: null | number; year?: number }>
): YearlyConsumption[] {
  return dataForChart
    .filter((row) => isNumeric(row.primaryModernized))
    .map((row) => ({ consumption: row.primaryModernized ?? 0, year: row.year ?? 0 }));
}

function getEnergyRuns(sorted: YearlyConsumption[], conf: EnergyClassConfItem[]): EnergyRun[] {
  const runs: EnergyRun[] = [];
  for (const item of sorted) {
    const classItem = getEnergyClassItem({
      conf,
      energyClass: undefined,
      energyConsumption: item.consumption,
    });
    if (!classItem) continue;
    const last = runs[runs.length - 1];
    if (last && last.classItem?.rating === classItem.rating) {
      last.endYear = item.year;
    } else {
      runs.push({ classItem, endYear: item.year, startYear: item.year });
    }
  }
  return runs;
}

function toTimelineConf(
  energyClasses: Array<{ bgColor?: string; max?: number; rating?: string; textColor?: string }>
): EnergyClassConfItem[] {
  if (!energyClasses?.length) return [];
  const numeric: EnergyClassConfItem[] = energyClasses.map((c) => ({
    bgColor: c.bgColor ?? "#ccc",
    max: Number(c.max),
    rating: c.rating ?? "",
    textColor: c.textColor ?? "#000",
  }));
  const last = numeric[numeric.length - 1];
  if (last && Number.isFinite(last.max)) numeric.push({ ...last, max: Infinity });
  return numeric;
}

export const EnergyClassTimeline = ({ maxYear = 2050, minYear = 2022 }: { maxYear?: number; minYear?: number }) => {
  const { dataForChart, energyClasses } = useGetEnergyEfficiencyClassesTimelineData();

  const consumptionData: YearlyConsumption[] = dataForChartToYearlyConsumption(dataForChart ?? []);

  const conf = toTimelineConf(energyClasses ?? []);
  const filtered = consumptionData.filter((d) => d.year >= minYear && d.year <= maxYear);
  const sorted = [...filtered].sort((a, b) => a.year - b.year);
  const runs = getEnergyRuns(sorted, conf);
  const range = maxYear - minYear || 1;

  return (
    <div className="mb-4 w-full px-4">
      <div className="relative min-h-10 overflow-hidden">
        {/* Colored segments: positioned by year % so they align with the Building Events timeline */}
        {runs.map((run, i) => {
          const leftPct = calculateLeft(run.startYear, minYear, maxYear);
          const widthPct = (100 * (run.endYear - run.startYear + 1)) / range;
          return (
            <div
              className={cn(
                "absolute top-6 h-2",
                i === 0 && "rounded-l-full",
                i === runs.length - 1 && "rounded-r-full"
              )}
              key={`seg-${run.startYear}-${run.endYear}`}
              style={{
                backgroundColor: (run.classItem?.bgColor as string) ?? "transparent",
                left: `${leftPct}%`,
                width: `${widthPct}%`,
              }}
              title={`${run.startYear}–${run.endYear}: ${run.classItem?.rating}`}
            />
          );
        })}
        {/* Labels: only at initial run and when energy class actually changes */}
        {runs.map((run, i) => {
          const prevRating = i > 0 ? runs[i - 1].classItem?.rating : undefined;
          const currRating = run.classItem?.rating;
          const isInitial = i === 0;
          const isNewLabel = isInitial || (i > 0 && prevRating !== currRating);
          if (!isNewLabel) return null;
          const leftPct = calculateLeft(run.startYear, minYear, maxYear);
          const isFirst = run.startYear === minYear;
          const isLast = run.endYear === maxYear && runs.length > 1;
          return (
            <div
              className={cn(
                "absolute left-0 top-0 flex flex-col",
                isFirst && "items-start",
                isLast && "-translate-x-full items-end",
                !isFirst && !isLast && "-translate-x-1/2 items-center"
              )}
              key={`label-${run.startYear}-${run.endYear}`}
              style={{ left: `${leftPct}%` }}
            >
              {run.classItem && (
                <EnergyEfficiencyClass
                  conf={conf}
                  energyClass={run.classItem.rating as string}
                  energyConsumption={undefined}
                  size="sm"
                  style={{ minHeight: 28, minWidth: 28 }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
